import { ReactElement, useRef, useState, useEffect } from 'react';
import { BottomDrawerModel, MultiDateRangePicker, Dropdown } from '@components/common';
import { useFormik } from 'formik';
import { useOutsideDialogClose } from '@hooks/useOutsideDialogClose';
import { LmsUserListFilterObject, useLmsUserListFilterStore } from '@store/lms/userListFilterStore';
import { useSSDropdownRxDb } from '@services/hooks/lms/addNewSite/useSSDropdownRxDb';
import { ISSListAPIResponse } from '@services/hooks/lms/addNewSite';

interface IUserListFiltersProps {
	openFilters: boolean;
	closeFilters: () => void;
	hideFilters?: boolean;
	hideLeadByFilter?: boolean;
}

export const UserListFilters = ({
	openFilters,
	closeFilters,
	hideFilters,
	hideLeadByFilter,
}: IUserListFiltersProps): ReactElement => {
	const dialogRef = useRef<HTMLDivElement | null>(null);
	useOutsideDialogClose({ dialogRef, callBackFunction: closeFilters });

	const { updateFilter, resetFilter, lmsUserListFilter } = useLmsUserListFilterStore();

	const [ssListOptions, setSSListOptions] = useState<{ label: string; value: string }[]>([]);

	const { data: ssList } = useSSDropdownRxDb(); // Read from RxDB

	useEffect(() => {
		const mapped = (ssList ?? []).map((ss: ISSListAPIResponse) => ({
			label: ss.mdicode_c,
			value: ss.mdicode_c,
		}));
		setSSListOptions(mapped);
	}, [ssList]);

	const formik = useFormik({
		initialValues: lmsUserListFilter,
		onSubmit: (values: LmsUserListFilterObject) => {
			updateFilter(values);
			closeFilters();
		},
	});

	const handleSubmit = (): void => {
		formik.handleSubmit();
	};

	const clearAll = (): void => {
		resetFilter();
		closeFilters();
	};

	const isFilterChecked =
		formik.values.createddate[0] !== null ||
		formik.values.createddate[1] !== null ||
		formik.values.status !== '' ||
		formik.values.competition_tag !== false ||
		formik.values.rigidGluePotential !== false ||
		(!!formik.values.source && formik.values.source.trim() !== '') ||
		!!formik.values.source; // also check SS list filter

	const statusOptions = ['Enrolled', 'Pending', 'Rejected'];

	return (
		<BottomDrawerModel openModelStatus={openFilters} modelId="filter-main-wrapper">
			<div className="container">
				<button
					type="button"
					aria-label="filter popup"
					className="filter-sort-icon icon-filter"
					onClick={closeFilters}
				></button>
				<div ref={dialogRef} className="filter-drawer-inner-wrapper filter">
					<span className="filter-title">
						<p className="h2">Filter</p>
						<button
							className="clear-all"
							type="button"
							hidden={!isFilterChecked}
							onClick={clearAll}
							aria-label="Clear All"
						>
							Clear All
						</button>
					</span>

					<form>
						<div className="form-fieldset row">
							<div className="d-col">
								<span className="form-title h4">
									<span className="icon-date"></span>Lead Creation Date
								</span>
								<MultiDateRangePicker
									id="createddate"
									name="createddate"
									value={formik.values.createddate}
									formik={formik}
									maxDate={new Date()}
								/>
							</div>
						</div>

						{!hideLeadByFilter && (
							<div className="form-fieldset row">
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-user"></span>Lead Submitted by
									</span>
									<Dropdown
										name="source"
										id="source"
										title={
											ssListOptions.find((opt) => opt.value === formik.values.source)?.label || 'Select Site Specialist'
										}
										options={ssListOptions}
										setFieldValue={formik.setFieldValue}
									/>
								</div>
							</div>
						)}

						{!hideFilters && (
							<div className="form-fieldset row">
								<div className="d-col">
									<span className="form-title h4">
										<span className="icon-hammer"></span> Contractor Status
									</span>
									<ul className="custom-check primary">
										{statusOptions.map((status) => (
											<li className="custom-check-item" key={status}>
												<label>
													<input
														type="radio"
														name="status"
														value={status}
														checked={formik.values.status === status}
														onChange={() =>
															formik.setFieldValue('status', formik.values.status === status ? '' : status)
														}
													/>
													<span className="h4">{status}</span>
												</label>
											</li>
										))}
									</ul>
								</div>
							</div>
						)}

						{/* Competition Contractor */}
						<div className="form-fieldset row">
							<div className="d-col">
								<ul className="custom-toggle-wrapper">
									<li>
										<span className="form-title h4">
											<span className="icon-user"></span>
											Competition Contractor
										</span>
										<ul className="custom-toggle">
											<li className="custom-toggle-item">
												<input
													type="checkbox"
													id="competition_tag"
													checked={formik.values.competition_tag}
													onChange={(e) => formik.setFieldValue('competition_tag', e.target.checked)}
												/>
												<label htmlFor={`competition_tag`}>
													<span className="visually-hidden">Competition Contractor</span>
												</label>
											</li>
										</ul>
									</li>
								</ul>
							</div>
						</div>

						{/* Rigid Contractor glue potential */}
						<div className="form-fieldset row">
							<div className="d-col">
								<ul className="custom-toggle-wrapper">
									<li key="Rigid Contractor glue potential" className="rigid-glue-potential-filter">
										<span className="form-title h4">
											<span className="icon-user"></span>
											Glue Consumption {'>='} 50
										</span>
										<ul className="custom-toggle">
											<li className="custom-toggle-item">
												<input
													type="checkbox"
													id="lms-rigid-glue-potential"
													checked={formik.values.rigidGluePotential}
													onChange={(e) => formik.setFieldValue('rigidGluePotential', e.target.checked)}
												/>
												<label htmlFor="lms-rigid-glue-potential">
													<span className="visually-hidden">Rigid Contractor glue potential</span>
												</label>
											</li>
										</ul>
									</li>
								</ul>
							</div>
						</div>
					</form>

					{/* Submit */}
					<div className="d-col submit">
						<button
							type="submit"
							disabled={!isFilterChecked}
							onClick={handleSubmit}
							className="btn btn-primary btn-medium"
						>
							Submit
						</button>
					</div>
				</div>
			</div>
		</BottomDrawerModel>
	);
};
